import React, { useCallback, useState, useMemo, useEffect } from 'react'
import { Table, Button, Tag, message } from 'antd'
import { EditOutlined, PlusOutlined } from '@ant-design/icons'
import AddRequisitesModal from './add-requisites-modal'
import fetchAPI from '../../../../../lib/utils/fetch-api'

interface ILegalData {
  id?: number | null
  name?: string
  organization_from?: number
  address?: string
  director?: string
  inn?: string
  invoice_curr?: string
  invoice_corr?: string
  bank_name?: string
  bik?: string
  okpo?: string
  kpp?: string
  ogrn?: string
  active?: boolean
  certificate_number?: string
}

interface LegalTabProps {
  legalData: ILegalData[]
  onChange: (data: ILegalData[]) => void
  editMode: (val: boolean) => void
  customerId: string
}

const getOrganizationTypeName = (type?: number) => {
  switch (type) {
    case 1:
      return 'ООО'
    case 2:
      return 'ИП'
    case 3:
      return 'АНО'
    default:
      return '-'
  }
}

const TabLegal: React.FC<LegalTabProps> = ({ legalData, onChange, editMode, customerId }) => {
  const [localData, setLocalData] = useState<ILegalData[]>(legalData)
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [editingRecord, setEditingRecord] = useState<ILegalData | null>(null)
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    setLocalData(legalData)
  }, [legalData])

  useEffect(() => {
    editMode(false)
  }, [editMode])

  const showAddModal = useCallback(() => {
    setEditingRecord(null)
    setIsModalVisible(true)
  }, [])

  const showEditModal = useCallback((record: ILegalData) => {
    setEditingRecord(record)
    setIsModalVisible(true)
  }, [])

  const handleModalCancel = useCallback(() => {
    setIsModalVisible(false)
    setEditingRecord(null)
  }, [])

  const handleModalSave = useCallback(async (requisite: ILegalData) => {
    setIsSaving(true)
    
    try {
      let nextData: ILegalData[]
      
      if (editingRecord) {
        nextData = localData.map(item =>
          item.id === requisite.id ? requisite : item
        )
      } else {
        const newId = localData.length ? Math.max(...localData.map(item => Number(item.id) || 0)) + 1 : 1
        const newRow: ILegalData = {
          ...requisite,
          id: newId,
        }
        nextData = [newRow, ...localData]
      }
      
      const result = await fetchAPI(`/api/customers/${customerId}`, {
        method: 'POST',
        body: JSON.stringify({ legal: nextData })
      })
      
      if (result?.error || result?.errors) {
        const errorMessage = result?.errors?.map((e: any) => e?.detail).join(', ') || 'Ошибка при сохранении'
        message.error(errorMessage)
        return
      }
      
      setLocalData(nextData)
      onChange(nextData)
      setIsModalVisible(false)
      setEditingRecord(null)
      message.success(editingRecord ? 'Реквизиты успешно обновлены' : 'Реквизиты успешно добавлены')
    } catch (error) {
      console.error('Error saving legal data:', error)
      message.error('Ошибка при сохранении реквизитов')
    } finally {
      setIsSaving(false)
    }
  }, [localData, onChange, editingRecord, customerId])

  const columns: any = useMemo(
    () => [
      { 
        title: 'Статус', 
        dataIndex: 'active', 
        width: 90,
        render: (active: boolean) => (
          <Tag color={active ? 'green' : 'default'}>
            {active ? 'Активен' : 'Неактивен'}
          </Tag>
        ),
      },
      { 
        title: 'Название', 
        dataIndex: 'name', 
        width: 200,
        ellipsis: true,
      },
      { 
        title: 'Юр. статус', 
        dataIndex: 'organization_from', 
        width: 100,
        render: (value: number) => getOrganizationTypeName(value),
      },
      { 
        title: 'Адрес', 
        dataIndex: 'address', 
        width: 250,
        ellipsis: true,
      },
      { 
        title: 'Директор', 
        dataIndex: 'director', 
        width: 200,
        ellipsis: true,
      },
      { 
        title: 'ИНН', 
        dataIndex: 'inn', 
        width: 130,
      },
      { 
        title: 'КПП', 
        dataIndex: 'kpp', 
        width: 110,
      },
      { 
        title: 'ОГРН', 
        dataIndex: 'ogrn', 
        width: 150,
      },
      { 
        title: 'Расчетный счет', 
        dataIndex: 'invoice_curr', 
        width: 180,
      },
      { 
        title: 'Корр. счет', 
        dataIndex: 'invoice_corr', 
        width: 180,
      },
      { 
        title: 'Банк', 
        dataIndex: 'bank_name', 
        width: 200,
        ellipsis: true,
      },
      { 
        title: 'БИК', 
        dataIndex: 'bik', 
        width: 110,
      },
      { 
        title: 'ОКПО', 
        dataIndex: 'okpo', 
        width: 110,
      },
      { 
        title: 'Свидетельство', 
        dataIndex: 'certificate_number', 
        width: 140,
      },
      {
        title: '',
        width: 50,
        fixed: 'right' as const,
        render: (_: any, record: ILegalData) => (
          <Button
            type='link'
            icon={<EditOutlined />}
            onClick={() => showEditModal(record)}
          />
        ),
      },
    ],
    [showEditModal]
  )

  return (
    <>
      <Button
        onClick={showAddModal}
        style={{ marginBottom: 10 }}
        type='primary'
        icon={<PlusOutlined />}
      >
        Добавить
      </Button>
      
      <Table
        bordered
        size='small'
        rowKey='id'
        dataSource={localData}
        columns={columns}
        pagination={{ 
          pageSize: 10,
          showSizeChanger: true,
          showTotal: (total) => `Всего: ${total}`,
        }}
        scroll={{ x: 2200 }}
      />
      
      <AddRequisitesModal
        visible={isModalVisible}
        onCancel={handleModalCancel}
        onSave={handleModalSave}
        editingData={editingRecord}
        isSaving={isSaving}
      />
    </>
  )
}

export default React.memo(TabLegal)
